import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import THEME from 'theme';
import { Modal } from 'components';
import { Heading, Text } from 'components/chroma';

import { Excess } from 'scenes/common/types';
import { replaceSpaces } from 'scenes/common/helpers/text';

import { ExcessCode, MotorCoverType } from 'scenes/motor/types';

import {
  excessDescriptionHeading,
  getExcessData,
  getAgedExcessData,
  hasAgeExcess,
  isAgeExcess,
} from '../ExcessUtils';

const EXCESS_TYPE = 'Excess type';
const AGE_EXCESS = 'Age excess';

const Container = styled.div`
  margin-bottom: 1.5rem;
  tr {
    border-bottom: 1px solid ${THEME.FIELD.BORDER.LIGHT};
  }
  th,
  td {
    padding: 0.5rem 3rem 0.5rem 0;
  }
`;

const TableHeader: FunctionComponent<{
  label: string;
}> = ({ label }) => (
  <thead>
    <tr>
      <th scope="col" id={replaceSpaces(label)}>
        <Text weight={700} margin={{ bottom: 0 }}>
          {label}
        </Text>
      </th>
      <th scope="col" id={replaceSpaces(`${label}_amount`)}>
        <Text weight={700} margin={{ bottom: 0 }}>
          Amount
        </Text>
      </th>
    </tr>
  </thead>
);

const TableRow: FunctionComponent<{
  excessType: string;
  label: string;
  amount: number;
}> = ({ excessType, label, amount }) => (
  <tr>
    <td headers={replaceSpaces(label)}>
      <Text margin={{ bottom: 0 }}>{excessType}</Text>
    </td>
    <td headers={`${label}-amount`}>
      <Text margin={{ bottom: 0 }}>${amount}</Text>
    </td>
  </tr>
);

const ExcessHeader: FunctionComponent = ({ children }) => (
  <Heading size={2} margin={{ bottom: 1 }}>
    {children}
  </Heading>
);

const ExcessModal: FunctionComponent<{
  isOpen: boolean;
  excesses: Excess[];
  onClose: () => void;
}> = ({ isOpen, excesses, onClose }) => {
  let isComprehensive = false;

  if (excesses.length && excesses[0].name === MotorCoverType.Comprehensive) {
    isComprehensive = true;
  }

  return (
    <Modal header="What is an excess?" isOpen={isOpen} onClose={onClose}>
      <Text>
        An excess is the amount you may need to pay towards the cost of a claim,
        sometimes you may need to pay more than one excess, these are explained
        below.
      </Text>

      <Heading size={2} margin={{ top: 6, bottom: 0 }}>
        Excess details
      </Heading>
      <Container>
        <table>
          <TableHeader label={EXCESS_TYPE} />
          <tbody>
            {getExcessData(excesses).map((excess) => (
              <TableRow
                key={excess.type}
                label={EXCESS_TYPE}
                excessType={excess.type}
                amount={excess.amount}
              />
            ))}
          </tbody>
          {hasAgeExcess(excesses) && <TableHeader label={AGE_EXCESS} />}
          <tbody>
            {getAgedExcessData(excesses).map((excess) => (
              <TableRow
                key={excess.type}
                label={AGE_EXCESS}
                excessType={excess.type}
                amount={excess.amount}
              />
            ))}
          </tbody>
        </table>
      </Container>

      {excesses.some(
        (excess) => excess.code === ExcessCode.BaseAmt && excess.amount > 0
      ) && (
        <>
          <ExcessHeader>
            {excessDescriptionHeading[ExcessCode.BaseAmt]}
          </ExcessHeader>
          <Text>The Standard excess applies when you make a claim.</Text>
        </>
      )}

      {excesses.some(
        (excess) => excess.code === ExcessCode.VoluntaryAmt && excess.amount > 0
      ) &&
        !isComprehensive && (
          <>
            <ExcessHeader>
              {excessDescriptionHeading[ExcessCode.VoluntaryAmt]}
            </ExcessHeader>
            <Text>
              If you have Comprehensive cover you may choose to vary your
              standard excess. You reduce your premium if you choose a higher
              Voluntary excess. This means you choose to pay a lesser premium,
              but you pay a higher excess if you need to make a claim.
            </Text>
            <Text>or</Text>
            <Text>
              You increase your premium if you choose a lower Voluntary excess.
              This means you choose to pay a higher premium, but you pay a lower
              excess if you need to make a claim. The Voluntary excess is not
              available for Third Party Property, Fire and Theft cover; and
              Third Party Property Damage cover. You have a Standard excess
              instead.
            </Text>
          </>
        )}

      {isComprehensive && (
        <>
          <ExcessHeader>Nominated excess</ExcessHeader>
          <Text>
            If you have Comprehensive cover you may choose to vary your standard
            excess. You reduce your premium if you choose a higher Nominated
            excess. This means you choose to pay a lesser premium, but you pay a
            higher excess if you need to make a claim.
          </Text>
          <Text>or</Text>
          <Text>
            You increase your premium if you choose a lower Nominated excess.
            This means you choose to pay a higher premium, but you pay a lower
            excess if you need to make a claim. The Nominated excess is not
            available for Third Party Property, Fire and Theft cover; and Third
            Party Property Damage cover. You have a Standard excess instead.
          </Text>
        </>
      )}

      {excesses.some(
        (excess) => isAgeExcess(excess.code) && excess.amount > 0
      ) && (
        <>
          <ExcessHeader>{AGE_EXCESS}</ExcessHeader>
          <Text>
            The Age excess applies when the driver of your car is under 25 years
            of age at the time of the collision. The Age excess is in addition
            to the other excesses (except the Inexperienced driver excess).
          </Text>
        </>
      )}

      {excesses.some(
        (excess) =>
          excess.code === ExcessCode.InexperienceExcess && excess.amount > 0
      ) && (
        <>
          <ExcessHeader>
            {excessDescriptionHeading[ExcessCode.InexperienceExcess]}
          </ExcessHeader>
          <Text>
            The Inexperienced driver excess applies when the driver of your car
            is 25 or older and has not held an Australian drivers&#39; licence
            for more than two years at the time of the collision. This excess
            may not apply if your current overseas licence is recognised by the
            Road Traffic authority within your state. The Inexperienced driver
            excess is in addition to the other excesses (except the Age excess).
          </Text>
        </>
      )}
    </Modal>
  );
};

export default ExcessModal;
